import { actualImageAssets, searchActualAssets } from '../utils/workingImageAssets';
import { ensureFreshAitryxCoreToken } from './aitryxCoreTokenService';
import axios from 'axios';
import { config } from '../config/environment';

// Image asset types
export interface ImageAsset {
  filename: string;
  displayName: string;
  category: string;
  subcategory?: string;
  url: string;
  thumbnailUrl?: string;
  description?: string;
  lastModified?: string;
  etag?: string;
}

export interface ImageCategory {
  name: string;
  displayName: string;
  subcategories: ImageSubcategory[];
}

export interface ImageSubcategory {
  name: string;
  displayName: string;
  assets: ImageAsset[];
}

export interface ImageAssetsResponse {
  categories: ImageCategory[];
  totalAssets: number;
}

// Marketplace API URL from centralized config
const MARKETPLACE_API_URL = config.api.marketplaceUrl;

// IndexedDB cache for assets with ETag support
const DB_NAME = 'aitryx-asset-cache';
const DB_VERSION = 1;
const STORE_NAME = 'assets';

interface CachedCategory {
  categoryName: string;
  data: ImageCategory;
  etag?: string;
  lastFetched: number;
}

/**
 * Open IndexedDB for asset caching
 */
async function openAssetCacheDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'categoryName' });
      }
    };
  });
}

/**
 * Get cached category from IndexedDB
 */
async function getCachedCategory(categoryName: string): Promise<CachedCategory | null> {
  try {
    const db = await openAssetCacheDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(categoryName);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result || null);
    });
  } catch (error) {
    console.warn('Failed to get cached category:', error);
    return null;
  }
}

/**
 * Save category to IndexedDB cache
 */
async function setCachedCategory(cached: CachedCategory): Promise<void> {
  try {
    const db = await openAssetCacheDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(cached);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  } catch (error) {
    console.warn('Failed to cache category:', error);
  }
}

/**
 * Get authenticated axios client for marketplace function app
 */
async function getMarketplaceClient() {
  const token = await ensureFreshAitryxCoreToken();
  return axios.create({
    baseURL: MARKETPLACE_API_URL,
    timeout: 60000,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });
}

/**
 * Image Assets Service
 * Manages access to partner-specific assets in Azure blob storage
 */
export class ImageAssetsService {
  
  /**
   * Get all available image assets from partner storage
   */
  async getAvailableAssets(): Promise<ImageAssetsResponse> {
    try {
      // Load all categories in parallel
      const categoryNames = ['hardware', 'software', 'generic', 'telco', 'custom'];
      const categories = await Promise.all(
        categoryNames.map(name => this.getAssetsByCategory(name))
      );
      
      const validCategories = categories.filter((c): c is ImageCategory => c !== null);
      const totalAssets = validCategories.reduce((total, category) =>
        total + category.subcategories.reduce((subTotal, sub) => subTotal + sub.assets.length, 0), 0
      );

      return {
        categories: validCategories,
        totalAssets
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get assets for a specific category from partner storage
   * Uses IndexedDB cache with ETag-based revalidation
   */
  async getAssetsByCategory(categoryName: string): Promise<ImageCategory | null> {
    try {
      // Check cache first
      const cached = await getCachedCategory(categoryName);
      const cacheAge = cached ? Date.now() - cached.lastFetched : Infinity;
      const cacheMaxAge = 5 * 60 * 1000; // 5 minutes

      // If cache is fresh, return it
      if (cached && cacheAge < cacheMaxAge) {
        return cached.data;
      }

      // Try the partner assets API
      try {
        const client = await getMarketplaceClient();
        const response = await client.get(`/assets/partner/category/${categoryName}`);
        
        if (response.data?.subcategories) {
          const cat: ImageCategory = {
            name: response.data.name,
            displayName: response.data.displayName,
            subcategories: (response.data.subcategories || []).map((s: any) => ({
              name: s.name,
              displayName: s.displayName,
              assets: (s.assets || []).map((a: any) => ({
                filename: a.filename,
                displayName: a.displayName,
                category: a.category,
                subcategory: a.subcategory,
                url: a.url,
                thumbnailUrl: a.thumbnailUrl,
                description: a.description,
                lastModified: a.lastModified,
                etag: a.eTag
              }))
            }))
          };
          
          // Cache the result
          await setCachedCategory({
            categoryName,
            data: cat,
            lastFetched: Date.now()
          });
          
          return cat;
        }
      } catch (apiError) {
        // Return cached data if available (even if stale)
        if (cached) {
          return cached.data;
        }
      }

      // Fallback to static asset list
      // Handle special case: 'telstra' category should return telstra subcategory from hardware
      if (categoryName === 'telstra' || categoryName === 'telco') {
        const hardwareCategory = actualImageAssets.find(cat => cat.name === 'hardware');
        if (hardwareCategory) {
          const telstraSubcategory = hardwareCategory.subcategories.find(sub => sub.name === 'telstra');
          if (telstraSubcategory) {
            return {
              name: 'telstra',
              displayName: 'Telstra',
              subcategories: [telstraSubcategory]
            };
          }
        }
        return null;
      }
      
      const category = actualImageAssets.find(cat => cat.name === categoryName);
      return category || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Upload a custom image to partner storage
   */
  async uploadCustomImage(
    file: File,
    options: { category?: string; subcategory?: string } = {}
  ): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      const client = await getMarketplaceClient();
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('filename', file.name);
      formData.append('category', options.category || 'custom');
      formData.append('subcategory', options.subcategory || 'uploads');
      
      const response = await client.post('/assets/partner/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data?.success) {
        // Invalidate cache for custom category
        await this.invalidateCategoryCache('custom');
        
        return { success: true, url: response.data.url };
      }
      
      return { success: false, error: response.data?.error || 'Upload failed' };
    } catch (error: any) {
      return { success: false, error: error.message || 'Upload failed' };
    }
  }

  /**
   * Delete a custom image from partner storage
   */
  async deleteCustomImage(path: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Ensure path starts with custom/
      const deletePath = path.startsWith('custom/') ? path : `custom/${path}`;
      
      const client = await getMarketplaceClient();
      const response = await client.delete(`/assets/partner/${deletePath}`);
      
      if (response.data?.success) {
        // Invalidate cache for custom category
        await this.invalidateCategoryCache('custom');
        
        return { success: true };
      }
      
      return { success: false, error: response.data?.error || 'Delete failed' };
    } catch (error: any) {
      return { success: false, error: error.message || 'Delete failed' };
    }
  }

  /**
   * Invalidate cache for a specific category
   */
  async invalidateCategoryCache(categoryName: string): Promise<void> {
    try {
      const db = await openAssetCacheDB();
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      store.delete(categoryName);
    } catch (error) {
      // Ignore cache invalidation failures
    }
  }

  /**
   * Get asset URL - assets from partner API already have SAS URLs
   */
  async getAssetUrl(assetPath: string): Promise<string> {
    // If already a full https URL with query (SAS token), return as-is
    if (/^https?:\/\//i.test(assetPath) && assetPath.includes('?')) {
      return assetPath;
    }
    
    // If it's an https URL without SAS, return as-is (might be a public blob)
    if (/^https?:\/\//i.test(assetPath)) {
      return assetPath;
    }
    
    // For relative paths, we can't resolve them without knowing the category
    // The caller should use getAssetsByCategory instead
    return assetPath;
  }

  /**
   * Search assets by name or description
   */
  async searchAssets(query: string, category?: string): Promise<ImageAsset[]> {
    try {
      // First try to search from cached/loaded categories
      const allCategories = category 
        ? [await this.getAssetsByCategory(category)]
        : await Promise.all(['hardware', 'software', 'generic', 'custom'].map(c => this.getAssetsByCategory(c)));
      
      const validCategories = allCategories.filter((c): c is ImageCategory => c !== null);
      const allAssets = validCategories.flatMap(cat => 
        cat.subcategories.flatMap(sub => sub.assets)
      );
      
      const queryLower = query.toLowerCase();
      const results = allAssets.filter(asset => 
        asset.displayName.toLowerCase().includes(queryLower) ||
        asset.filename.toLowerCase().includes(queryLower) ||
        asset.description?.toLowerCase().includes(queryLower)
      );
      
      if (results.length > 0) {
        return results;
      }
      
      // Fallback to static list search
      return searchActualAssets(query, category);
    } catch (error) {
      return searchActualAssets(query, category);
    }
  }

  /**
   * Utility to generate display name from filename
   */
  generateDisplayName(filename: string): string {
    return filename
      .replace(/\.(png|jpg|jpeg|gif|webp)$/i, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
      .trim();
  }

  /**
   * Basic categorization helper
   */
  categorizeAsset(filename: string): { category: string; subcategory?: string } {
    const lower = filename.toLowerCase();
    if (lower.includes('iphone') || lower.includes('ipad') || lower.includes('mac')) {
      return { category: 'hardware', subcategory: 'apple' };
    }
    if (lower.includes('pixel') || lower.includes('chrome') || lower.includes('nest')) {
      return { category: 'hardware', subcategory: 'google' };
    }
    if (lower.includes('galaxy') || lower.includes('samsung')) {
      return { category: 'hardware', subcategory: 'samsung' };
    }
    if (lower.includes('telstra')) {
      return { category: 'hardware', subcategory: 'telstra' };
    }
    if (lower.includes('icon') || lower.includes('logo')) {
      return { category: 'software', subcategory: 'generic' };
    }
    return { category: 'hardware', subcategory: 'generic' };
  }
}

// Export singleton instance
export const imageAssetsService = new ImageAssetsService();
